import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaInstagram,
  FaPaperPlane,
  FaClock,
  FaMapMarkerAlt,
} from "react-icons/fa";
import PolicyModal from "./PolicyModal";
import ContactModal from "./ContactModal";
import useScrollTo from "../hooks/useScrollTo";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [modalState, setModalState] = useState({
    isOpen: false,
    policyType: null,
  });
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { scrollToSection } = useScrollTo();
  const navigate = useNavigate();

  const handleQuickLink = (link) => {
    if (link.route) {
      navigate(link.route);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      scrollToSection(link.id);
    }
  };

  const openPolicy = (policyType) => {
    setModalState({ isOpen: true, policyType });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, policyType: null });
  };

  const quickLinks = [
    { id: "home", label: "Home" },
    { id: "services", label: "Services" },
    { id: "our-work", label: "Our Work", route: "/our-work" },
    { id: "about", label: "About" },
    { id: "consultation", label: "Free Consultation" },
  ];

  const services = [
    { label: "Custom Software Development", route: "/services/custom-software-development" },
    { label: "Web Development", route: "/services/web-development" },
    { label: "Mobile App Development", route: "/services/mobile-app-development" },
    { label: "SEO Services", route: "/services/seo-services" },
    { label: "IT Consulting", route: "/services/it-consulting" },
    { label: "Business Automation", route: "/services/business-automation" },
  ];

  const socialLinks = [
    {
      icon: <FaFacebookF />,
      url: "https://www.facebook.com/cititekpro",
      label: "Facebook",
      color: "hover:bg-blue-600",
    },

    {
      icon: <FaLinkedinIn />,
      url: "https://www.linkedin.com/company/cititekpro",
      label: "LinkedIn",
      color: "hover:bg-blue-700",
    },
    {
      icon: <FaInstagram />,
      url: "https://www.instagram.com/cititekpro",
      label: "Instagram",
      color: "hover:bg-pink-600",
    },
  ];

  const contactInfo = [
    {
      icon: <FaPaperPlane />,
      label: "Get in Touch",
      value: "Send us a message",
      isContactModal: true,
    },
    {
      icon: <FaClock />,
      label: "Business Hours",
      value: "Mon–Fri, 9:00 AM – 6:00 PM",
      link: null,
    },
    {
      icon: <FaMapMarkerAlt />,
      label: "Office",
      value: "2910 Pillsbury Ave, Suite 31\nMinneapolis, MN 55408",
      link: null,
    },
  ];

  return (
    <>
      <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 dark:from-black dark:via-gray-900 dark:to-black text-white overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-primary/20 rounded-full filter blur-3xl"></div>
          <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-teal/20 rounded-full filter blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-12">
            {/* Company Info */}
            <div className="space-y-6 animate-fade-in-up">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold gradient-text mb-3">
                  CitiTekPro
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  A full-service digital agency delivering enterprise-grade solutions
                  in custom software development, digital transformation, and
                  strategic IT consulting.
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-300 mb-3 uppercase tracking-wider">
                  Follow Us
                </h4>
                <div className="flex gap-3">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      className={`group p-3 bg-gray-800 dark:bg-gray-700 rounded-lg transition-all duration-300 ${social.color} hover:scale-110 hover:shadow-lg`}
                    >
                      <span className="text-white">{social.icon}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div
              className="animate-fade-in-up"
              style={{ animationDelay: "0.1s" }}
            >
              <h4 className="text-lg font-bold mb-6 flex items-center gap-2">
                 {/* <span className="w-1 h-6 bg-gradient-to-b from-primary to-teal rounded-full"></span> */}
                Quick Links
              </h4>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.id}>
                    <button
                      onClick={() => handleQuickLink(link)}
                      className="group flex items-center text-gray-400 hover:text-white transition-colors duration-300"
                    >
                      <span className="w-0 h-0.5 bg-gradient-to-r from-primary to-teal group-hover:w-4 transition-all duration-300 mr-0 group-hover:mr-2"></span>
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div
              className="animate-fade-in-up"
              style={{ animationDelay: "0.2s" }}
            >
              <h4 className="text-lg font-bold mb-6 flex items-center gap-2">
                 {/*  <span className="w-1 h-6 bg-gradient-to-b from-teal to-accent rounded-full"></span>  */}
                Our Services
              </h4>
              <ul className="space-y-3">
                {services.map((service, index) => (
                  <li key={index}>
                    <Link
                      to={service.route}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="group flex items-center text-gray-400 hover:text-white transition-colors duration-300"
                    >
                      <span className="w-0 h-0.5 bg-gradient-to-r from-teal to-accent group-hover:w-4 transition-all duration-300 mr-0 group-hover:mr-2"></span>
                      {service.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div
              className="animate-fade-in-up"
              style={{ animationDelay: "0.3s" }}
            >
              <h4 className="text-lg font-bold mb-6 flex items-center gap-2">
              {/*  <span className="w-1 h-6 bg-gradient-to-b from-accent to-orange-600 rounded-full"></span> */}
                Contact Us
              </h4>
              <ul className="space-y-4">
                {contactInfo.map((info, index) => (
                  <li key={index} className="group">
                    {info.isContactModal ? (
                      <button
                        onClick={() => setIsContactModalOpen(true)}
                        className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors duration-300 cursor-pointer"
                      >
                        <div className="flex-shrink-0 p-2 bg-gray-800 dark:bg-gray-700 rounded-lg group-hover:bg-gradient-to-br group-hover:from-primary group-hover:to-teal transition-all duration-300">
                          {info.icon}
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">
                            {info.label}
                          </p>
                          <p className="text-sm mt-1">{info.value}</p>
                        </div>
                      </button>
                    ) : info.link ? (
                      <a
                        href={info.link}
                        className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors duration-300"
                      >
                        <div className="flex-shrink-0 p-2 bg-gray-800 dark:bg-gray-700 rounded-lg group-hover:bg-gradient-to-br group-hover:from-primary group-hover:to-teal transition-all duration-300">
                          {info.icon}
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">
                            {info.label}
                          </p>
                          <p className="text-sm mt-1">{info.value}</p>
                        </div>
                      </a>
                    ) : (
                      <div className="flex items-start gap-3 text-gray-400">
                        <div className="flex-shrink-0 p-2 bg-gray-800 dark:bg-gray-700 rounded-lg">
                          {info.icon}
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">
                            {info.label}
                          </p>
                          <p className="text-sm mt-1 whitespace-pre-line">{info.value}</p>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent mb-8"></div>

          {/* Bottom Bar */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm text-center md:text-left">
              &copy; {currentYear} CitiTekPro. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <Link
                to="/login"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-gray-400 hover:text-white text-sm transition-colors duration-300 hover:underline underline-offset-4"
              >
                Login
              </Link>
              <button
                onClick={() => openPolicy("privacy")}
                className="text-gray-400 hover:text-white text-sm transition-colors duration-300 hover:underline underline-offset-4"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => openPolicy("terms")}
                className="text-gray-400 hover:text-white text-sm transition-colors duration-300 hover:underline underline-offset-4"
              >
                Terms of Service
              </button>
              <button
                onClick={() => openPolicy("cookie")}
                className="text-gray-400 hover:text-white text-sm transition-colors duration-300 hover:underline underline-offset-4"
              >
                Cookie Policy
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Policy Modal */}
      <PolicyModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        policyType={modalState.policyType}
        onOpenContactForm={() => setIsContactModalOpen(true)}
      />

      {/* Contact Modal */}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </>
  );
};

export default Footer;
